import { useEffect, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { climbsService } from '../../../services/climbs';
import type { RecentClimbPhoto } from '../../../types/climb';
import type { ClimbEditorPlace, ClimbEditorType } from './useClimbEditorRoute';

/**
 * The roles that put climbs up here — the same three the endpoint answers to
 * (`Location#climb_staff?`). Asked on the client as well as on the server so a
 * member creating a climb does not spend a request on a 403 they were never
 * going to pass.
 */
const CLIMB_ROLES = ['admin', 'editor', 'routesetter'];

/**
 * Whether the reader is one of this place's own climb staff — whoever the
 * place's last photos are offered to.
 */
export function useIsClimbStaff(locationSlug: string | undefined): boolean {
  const { user } = useAuth();
  const staff = user?.staff?.find((entry) => entry.gym_slug === locationSlug);

  return staff?.status === 'confirmed' && staff.staff_roles.some((role) => CLIMB_ROLES.includes(role));
}

interface RecentClimbPhotosArgs {
  place: ClimbEditorPlace;
  locationSlug: string | undefined;
  /** Absent until the place has loaded; the fetch waits for it. */
  locationId: string | undefined;
  type: ClimbEditorType;
}

/**
 * The wall photos this gym's last climbs were drawn on.
 *
 * A reset is a session: the setter shoots a wall once and puts up six problems
 * on it, so the picture for the next one is nearly always a picture the gym
 * already has. What comes back is the whole shot rather than what any of those
 * climbs saved, one entry per photo — see the controller.
 *
 * **Gyms, and only for their staff.** A crag offers its sectors' photos instead,
 * which is the same idea answered by the place itself; and a member putting up a
 * climb they have just done has no business browsing the gym's recent work.
 */
export function useRecentClimbPhotos({
  place,
  locationSlug,
  locationId,
  type,
}: RecentClimbPhotosArgs): RecentClimbPhoto[] {
  const [photos, setPhotos] = useState<RecentClimbPhoto[]>([]);
  const isClimbStaff = useIsClimbStaff(locationSlug);

  useEffect(() => {
    if (place !== 'gym' || !locationId || !isClimbStaff) {
      setPhotos([]);
      return;
    }

    let cancelled = false;
    climbsService
      .getRecentPhotos(locationId, type === 'Route' ? 'route' : 'boulder')
      // A gym that will not answer costs the setter a shortcut, not the page.
      .then((loaded) => {
        if (!cancelled) setPhotos(loaded.filter((photo) => !!photo.url));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [place, locationId, isClimbStaff, type]);

  return photos;
}
